import React from "react";
import {
    BaseEdge,
    EdgeLabelRenderer,
    EdgeProps,
    getSmoothStepPath,
} from "@xyflow/react";
import classNames from "classnames";
import colors from "@styles/colors.module.scss";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import {
    getBundleLabel,
    getEdgeLabel,
    selectionForEdge,
} from "../ownershipGraph.helpers";
import { pointsToRoundedPath } from "../ownershipEdgeRouting.helpers";
import { OwnershipFlowEdge, SELECTED_EDGE_Z } from "../ownership.types";
import { useEdgeSelection } from "./edgeSelection.context";
import styles from "./OwnershipEdge.module.scss";

export const OwnershipEdge: React.FC<EdgeProps<OwnershipFlowEdge>> = React.memo(
    ({
        id,
        source,
        target,
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        data,
        selected,
    }) => {
        const [fallbackPath, fallbackLabelX, fallbackLabelY] =
            getSmoothStepPath({
                sourceX,
                sourceY,
                targetX,
                targetY,
                sourcePosition,
                targetPosition,
                borderRadius: 8,
            });

        const routedPoints = data?.points;
        const path =
            routedPoints && routedPoints.length > 1
                ? pointsToRoundedPath(routedPoints, 8)
                : fallbackPath;
        const labelX = data?.labelAnchor?.x ?? fallbackLabelX;
        const labelY = data?.labelAnchor?.y ?? fallbackLabelY;

        const selectEdge = useEdgeSelection();
        const relationship = data?.relationship;
        const isDraft = data?.isDraft === true;
        const isOwnership =
            relationship?.type ===
            ConsolidationEntityRelationshipType.ownership;
        const bundledRelationships = data?.bundledRelationships ?? [];
        const isBundle = bundledRelationships.length > 1;
        const singleRelationships = relationship ? [relationship] : [];
        const singleLabel = relationship ? getEdgeLabel(relationship) : "";
        const labelText = isBundle
            ? getBundleLabel(bundledRelationships.length)
            : singleLabel;
        // Arrowheads reflect which directions exist in the (possibly bundled)
        // relationships: forward = payer is this edge's source, reverse = payer
        // is the target. A bidirectional bundle shows both heads.
        const edgeRelationships = isBundle
            ? bundledRelationships
            : singleRelationships;
        const hasForward = edgeRelationships.some(
            ({ sourceEntityUuid }) => sourceEntityUuid === source,
        );
        const hasReverse = edgeRelationships.some(
            ({ sourceEntityUuid }) => sourceEntityUuid === target,
        );
        const selectThisEdge = () => {
            selectEdge(selectionForEdge({ id, source, target, data }));
        };

        // The arrowhead color is baked into the SVG marker (it does not inherit
        // the edge's CSS stroke), so render per-edge markers recolored on
        // selection to match the edge stroke. A separate start marker (oriented
        // in reverse) draws the second head of a bidirectional bundle.
        // Bundle ids contain characters (e.g. ":") that break SVG `url(#…)`
        // marker references, so derive a safe id for the per-edge markers.
        const markerSuffix = selected ? "-selected" : "";
        const safeId = id.replace(/[^a-zA-Z0-9_-]/g, "-");
        const markerEndId = `ownership-arrow-end-${safeId}${markerSuffix}`;
        const markerStartId = `ownership-arrow-start-${safeId}${markerSuffix}`;
        let markerColor = colors.miscAlpha300NoAlpha;
        if (isDraft) {
            markerColor = colors.gray500;
        } else if (selected) {
            markerColor = colors.gray700;
        }

        return (
            <>
                <svg width={0} height={0}>
                    <defs>
                        <marker
                            id={markerEndId}
                            markerWidth={16}
                            markerHeight={8}
                            refX={4}
                            refY={4}
                            orient="auto"
                            markerUnits="userSpaceOnUse"
                        >
                            <path
                                d="M0,0 L4,4 L0,8"
                                fill="none"
                                stroke={markerColor}
                                strokeWidth={1}
                            />
                        </marker>
                        <marker
                            id={markerStartId}
                            markerWidth={16}
                            markerHeight={8}
                            refX={4}
                            refY={4}
                            orient="auto-start-reverse"
                            markerUnits="userSpaceOnUse"
                        >
                            <path
                                d="M0,0 L4,4 L0,8"
                                fill="none"
                                stroke={markerColor}
                                strokeWidth={1}
                            />
                        </marker>
                    </defs>
                </svg>
                <BaseEdge
                    id={id}
                    path={path}
                    markerStart={
                        hasReverse ? `url(#${markerStartId})` : undefined
                    }
                    markerEnd={hasForward ? `url(#${markerEndId})` : undefined}
                    className={classNames(styles.edge, {
                        [styles.edgeReference]: !isOwnership,
                        [styles.edgeDraft]: isDraft,
                        [styles.edgeSelected]: selected,
                    })}
                />
                {relationship && !isDraft && (
                    <>
                        {/* Transparent wide hit area so selection starts as soon
                            as a click begins anywhere on the edge. */}
                        <path
                            d={path}
                            fill="none"
                            stroke="transparent"
                            strokeWidth={20}
                            className={styles.hitArea}
                            onMouseDown={selectThisEdge}
                        />
                        <EdgeLabelRenderer>
                            <button
                                type="button"
                                className={classNames(styles.label, {
                                    [styles.labelSelected]: selected,
                                })}
                                style={{
                                    transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                                    // Match the edge's z so a selected edge's
                                    // label lifts with it, above unselected ones.
                                    zIndex: selected ? SELECTED_EDGE_Z : 0,
                                }}
                                onMouseDown={selectThisEdge}
                            >
                                {labelText}
                            </button>
                        </EdgeLabelRenderer>
                    </>
                )}
            </>
        );
    },
);
OwnershipEdge.displayName = "OwnershipEdge";
